<script setup lang="ts">
import { addPassage } from '~/apis/data'
import { useTextareaAutoHeight } from '~/composables/useTextareaAutoHeight'
import { MAX_PASSAGE_LENGTH } from '~/common/globals'

const app = useAppStore()
// 새 API composable 사용
const { translatePassage } = useNewApi()
interface PassageData {
  lineBreakType: 'remove' | 'keep' | ''
  title: string
  content: string
  koreanTranslation: string
}

interface Props {
  initialData?: Partial<PassageData>
}

interface Emits {
  (e: 'close'): void
  (e: 'add', data: PassageData): void
  (e: 'aiTranslate', content: string): void
}

const props = withDefaults(defineProps<Props>(), {
  initialData: () => ({}),
})

const emit = defineEmits<Emits>()
const dataLayerPopup = useDataLayerPopup()
const { show: showLoading, hide: hideLoading } = useLoading()

const lineBreakType = ref<'remove' | 'keep' | ''>('')
const passageTitle = ref('')
const passageContent = ref('')
const originalPassageContent = ref('')
const koreanTranslation = ref('')
const koreanTranslationTextarea = ref<HTMLTextAreaElement | null>(null)
const isTranslating = ref(false)
const isAddingPassage = ref(false)

// textarea 자동 높이 조절
useTextareaAutoHeight()

// textarea 높이 수동 조절 함수
const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
  if (!element) return

  let initialHeight = parseInt(element.style.height, 10)
  if (isNaN(initialHeight) || initialHeight <= 0) {
    initialHeight = parseInt(getComputedStyle(element).height, 10) || 120
  }

  element.style.height = `${initialHeight}px`
  const maxHeight = parseInt(getComputedStyle(element).maxHeight, 10) || 220
  element.style.height = `${Math.min(element.scrollHeight, maxHeight)}px`
  element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden'
}

const isFormValid = computed(() => {
  return (
    lineBreakType.value !== '' &&
    passageTitle.value.trim() !== '' &&
    passageContent.value.trim() !== '' &&
    koreanTranslation.value.trim() !== ''
  )
})

const displayPassageContent = computed(() => {
  if (lineBreakType.value === 'remove') {
    const processed = originalPassageContent.value
      .replace(/[\r\n]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    console.log('displayPassageContent - processed:', JSON.stringify(processed))
    return processed
  } else {
    return originalPassageContent.value
  }
})

watch(
  () => props.initialData,
  (newData) => {
    if (newData) {
      lineBreakType.value = newData.lineBreakType || ''
      passageTitle.value = newData.title || ''
      originalPassageContent.value = newData.content || ''
      passageContent.value = newData.content || ''
      koreanTranslation.value = newData.koreanTranslation || ''
    }
  },
  { immediate: true },
)

// lineBreakType 변경 시 passageContent 업데이트
watch(lineBreakType, (newType) => {
  if (originalPassageContent.value) {
    if (newType === 'remove') {
      passageContent.value = originalPassageContent.value
        .replace(/[\r\n]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    } else {
      passageContent.value = originalPassageContent.value
    }
    console.log('lineBreakType watch - newType:', newType)
    console.log(
      'lineBreakType watch - updated passageContent:',
      JSON.stringify(passageContent.value),
    )
  }
})

const handleCloseLayer = () => {
  dataLayerPopup.hideNewPassageInput()
}

const handleLineBreakTypeChange = () => {
  console.log('lineBreakTypeChanged:', lineBreakType.value)
  // displayPassageContent computed가 자동으로 업데이트됨
}

const handlePassageTitleChange = () => {
  // passageTitle이 reactive하므로 자동으로 업데이트됨
}

const handlePassageContentInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement
  const value = target.value

  // 원본 내용 저장
  originalPassageContent.value = value

  // 현재 줄바꿈 타입에 따라 처리된 내용을 passageContent에 저장
  if (lineBreakType.value === 'remove') {
    passageContent.value = value
      .replace(/[\r\n]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  } else {
    passageContent.value = value
  }

  console.log(
    'handlePassageContentInput - originalContent:',
    JSON.stringify(originalPassageContent.value),
  )
  console.log(
    'handlePassageContentInput - processedContent:',
    JSON.stringify(passageContent.value),
  )
}

const handleKoreanTranslationChange = () => {
  // koreanTranslation이 reactive하므로 자동으로 업데이트됨
}

const handleAiTranslateClick = async () => {
  if (passageContent.value.trim() && !isTranslating.value) {
    isTranslating.value = true
    showLoading()

    try {
      const response = await translatePassage({
        UM_ID: app.user.UM_ID,
        PASSAGE: passageContent.value.trim(),
      })

      console.log('Translation response:', response)

      // 번역 결과를 직접 한글 해석 필드에 설정
      // 새 Lambda API는 response.data 구조로 응답
      if (response?.data) {
        koreanTranslation.value = response.data
      } else if (response) {
        // 직접 응답인 경우
        koreanTranslation.value = response
      } else {
        koreanTranslation.value = '번역 결과를 받지 못했습니다.'
      }

      // 한글 해석 textarea 높이 조절
      await nextTick()
      if (koreanTranslationTextarea.value) {
        adjustTextareaHeight(koreanTranslationTextarea.value)
      }
    } catch (error) {
      console.error('Translation failed:', error)
      // 에러 발생 시 사용자에게 알림
      koreanTranslation.value = '번역에 실패했습니다. 다시 시도해주세요.'

      // 에러 메시지에 대해서도 textarea 높이 조절
      await nextTick()
      if (koreanTranslationTextarea.value) {
        adjustTextareaHeight(koreanTranslationTextarea.value)
      }
    } finally {
      hideLoading()
      isTranslating.value = false
    }

    emit('aiTranslate', passageContent.value)
  }
}

const handleAddPassageClick = async () => {
  if (isFormValid.value && !isAddingPassage.value) {
    isAddingPassage.value = true
    showLoading()
    const data: PassageData = {
      lineBreakType: lineBreakType.value,
      title: passageTitle.value.trim(),
      content: passageContent.value.trim(),
      koreanTranslation: koreanTranslation.value.trim(),
    }

    try {
      // API로 지문 추가
      const response = await addPassage({
        UM_ID: app.user.UM_ID,
        CRETURN_YN:
          data.lineBreakType === 'keep'
            ? 'Y'
            : data.lineBreakType === 'remove'
              ? 'N'
              : '',
        TITLE: data.title,
        PASSAGE: data.content,
        PASSAGE_KR: data.koreanTranslation || '',
        FOLDER_ID: dataLayerPopup.state.value.newPassageInput.folderId || '',
      })

      const callback = dataLayerPopup.state.value.newPassageInput.callback
      if (callback && response.data.data.length > 0) {
        const savedPassage = response.data.data[0]
        // 원본 텍스트와 처리된 텍스트 구분
        const originalContent = data.content
        const processedContent =
          data.lineBreakType === 'remove'
            ? originalContent.replace(/\n/g, ' ')
            : originalContent

        callback({
          title: savedPassage.edp_title,
          content: processedContent,
          originalContent: originalContent,
          edp_idx: savedPassage.edp_idx,
          reg_date: savedPassage.reg_date,
          passage_type: savedPassage.passage_type,
          passage_type_name: savedPassage.passage_type_name,
          passage_kr: savedPassage.passage_kr,
          creturn: savedPassage.creturn,
          creturn_name: savedPassage.creturn_name,
        })
      }

      emit('add', data)
      dataLayerPopup.hideNewPassageInput()
    } catch (error) {
      console.error('Failed to add passage:', error)
      // 에러 발생 시 fallback으로 입력된 데이터만 전달
      const callback = dataLayerPopup.state.value.newPassageInput.callback
      if (callback) {
        const originalContent = data.content
        const processedContent =
          data.lineBreakType === 'remove'
            ? originalContent.replace(/\n/g, ' ')
            : originalContent

        callback({
          title: data.title,
          content: processedContent,
          originalContent: originalContent,
        })
      }
      emit('add', data)
      dataLayerPopup.hideNewPassageInput()
    } finally {
      hideLoading()
      isAddingPassage.value = false
    }
  }
}
</script>
<template>
  <div class="layer active">
    <div class="mask" @click="handleCloseLayer"></div>
    <div class="inner">
      <section class="sample fullHeight">
        <header>
          <h2>새로운 지문 입력하기</h2>
          <a class="layerClose" @click="handleCloseLayer">닫기</a>
        </header>
        <main>
          <div class="writeType01">
            <dl>
              <dt>줄바꿈 유형</dt>
              <dd>
                <label class="label">
                  <input
                    type="radio"
                    name="lineBreakType"
                    value="remove"
                    v-model="lineBreakType"
                    @change="handleLineBreakTypeChange"
                  />
                  줄바꿈 제거
                </label>
                <label class="label">
                  <input
                    type="radio"
                    name="lineBreakType"
                    value="keep"
                    v-model="lineBreakType"
                    @change="handleLineBreakTypeChange"
                  />
                  줄바꿈 유지
                </label>
              </dd>
            </dl>
            <dl>
              <dt>지문 제목</dt>
              <dd>
                <input
                  type="text"
                  placeholder="지문 제목을 입력해주세요"
                  v-model="passageTitle"
                  @input="handlePassageTitleChange"
                />
              </dd>
            </dl>
            <dl>
              <dt>지문 내용</dt>
              <dd>
                <label class="textareaWrap">
                  <textarea
                    :placeholder="`지문 입력 주의사항 &#10;(1), (가), (A)와 같은 번호나 기호는 모두 제거해 주세요.&#10;${MAX_PASSAGE_LENGTH}자가 넘는 긴 지문은 나눠서 넣어주세요.&#10;오타, 철자 오류, 어법 오류는 수정해 올바른 문장으로 입력해 주세요.&#10;한글 내용, 특수 기호는 자동으로 삭제됩니다.`"
                    class="textareaAutoHeight"
                    :value="displayPassageContent"
                    @input="handlePassageContentInput"
                    :maxlength="MAX_PASSAGE_LENGTH"
                  ></textarea>
                  <span class="count">
                    <span>{{ passageContent.length }}</span>
                    <span>/</span>
                    {{ MAX_PASSAGE_LENGTH }}
                  </span>
                </label>
              </dd>
              <dd class="notice">
                주의사항을 지키지 않는 경우 문제 결과에 오류가 있을 수 있습니다.
              </dd>
            </dl>
            <dl>
              <dt>한글 해석</dt>
              <dd>
                <label class="textareaWrap">
                  <textarea
                    ref="koreanTranslationTextarea"
                    placeholder="한글 해석을 직접 입력하거나 AI로 번역해보세요."
                    class="textareaAutoHeight"
                    v-model="koreanTranslation"
                    @input="handleKoreanTranslationChange"
                  ></textarea>
                  <span></span>
                </label>
                <a
                  class="aiTranslate"
                  :class="{ loading: isTranslating }"
                  @click="handleAiTranslateClick"
                  :disabled="isTranslating"
                >
                  {{ isTranslating ? 'AI 번역 중...' : 'AI 번역하기' }}
                </a>
              </dd>
            </dl>
          </div>
        </main>
        <footer>
          <div class="btnWrap">
            <a
              class="btnStyle wide"
              :class="{ disable: !isFormValid || isAddingPassage }"
              @click="handleAddPassageClick"
              :disabled="isAddingPassage"
            >
              {{ isAddingPassage ? '추가 중...' : '추가하기' }}
            </a>
          </div>
        </footer>
      </section>
    </div>
  </div>
</template>
